import XKDK_ABI from 'abis/XKDK.json'
import { ButtonPrimary } from 'components/Button'
import Row from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import { useStepLineState, useStepLineStateUpdate } from 'components/StepLine/context/StepLineContext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { KODIAK_REWARDS_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useConnection } from 'hooks/useConnection'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useState } from 'react'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'

import AllocateStep from './AllocateStep'
import AllocateViewStep, { AllocationStepState } from './AllocateViewStep'
import { useApproveUsageStep } from './ApproveUsageStep'
import DeallocateStep from './DeallocateStep'
import DeallocateViewStep from './DeallocateViewStep'

const useAllocateState = () => {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const rewardsAddress = chainId ? KODIAK_REWARDS_ADDRESSES[chainId] : undefined
  const xKdkAddress = chainId ? XKDK_ADDRESSES[chainId] : undefined
  const { amount } = useStepLineState<AllocationStepState>()

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const onAllocate = async () => {
    if (!xKdkAddress || !amount || !rewardsAddress || !wagmiPublicClient || !account) return
    const quotient = amount.quotient.toString()

    setAttemptingTxn(true)
    try {
      const tx = {
        address: xKdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'allocate',
        args: [rewardsAddress, BigInt(quotient), '0x'],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHash(txHash)
      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.DIVIDENDS_ALLOCATE,
          amount: `${amount?.toSignificant(6)} ${amount?.currency?.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setAttemptingTxn(false)
    }
  }

  const reset = () => {
    setAttemptingTxn(false)
    setHash('')
  }

  return { amount, attemptingTxn, hash, onAllocate, reset }
}

const useDeallocateState = () => {
  const { chainId, account, wagmiPublicClient } = useConnection()
  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const xKdkAddress = XKDK_ADDRESSES[chainId]
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const { amount } = useStepLineState<AllocationStepState>()

  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const onDeallocate = async () => {
    if (!xKdkAddress || !amount || !rewardsAddress || !wagmiPublicClient || !account) return
    const quotient = amount.quotient.toString()

    setAttemptingTxn(true)
    try {
      const tx = {
        address: xKdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'deallocate',
        args: [rewardsAddress, BigInt(quotient), '0x'],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHash(txHash)
      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.DIVIDENDS_DEALLOCATE,
          amount: `${amount?.toSignificant(6)} ${amount?.currency?.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (e) {
      console.error(e)
    } finally {
      setAttemptingTxn(false)
    }
  }

  const reset = () => {
    setAttemptingTxn(false)
    setHash('')
  }

  return { amount, attemptingTxn, hash, onDeallocate, reset }
}

const useOnBack = () => {
  const update = useStepLineStateUpdate()
  return () =>
    update({
      '__nexts/AllocateViewStep': false,
      '__nexts/DeallocateViewStep': false,
    })
}

interface ManagerAllocationButtonsProps {
  isOpen?: 'allocate' | 'deallocate' | false
  setIsOpen?: (value: 'allocate' | 'deallocate' | false) => void
}

const ManagerAllocationButtons = ({
  isOpen: externalIsOpen,
  setIsOpen: setExternalIsOpen,
}: ManagerAllocationButtonsProps = {}) => {
  const { chainId } = useConnection()
  const rewardsAddress = KODIAK_REWARDS_ADDRESSES[chainId]
  const [internalIsOpen, setInternalIsOpen] = useState<false | 'allocate' | 'deallocate'>(false)

  const isModalOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen
  const setIsModalOpen = setExternalIsOpen || setInternalIsOpen

  const clear = () => setIsModalOpen(false)

  const onBack = useOnBack()
  const {
    amount: allocateAmount,
    attemptingTxn: allocateAttemptingTxn,
    hash: allocateHash,
    onAllocate,
    reset: resetAllocate,
  } = useAllocateState()
  const allocateStep = {
    ...useStepWithNext('AllocateViewStep', AllocateViewStep, {}),
    onBack,
  }
  const approveUsageStep = {
    ...useApproveUsageStep(allocateAmount, rewardsAddress),
    onBack,
  }
  const allocateConfirmStep = useStepWithNext('allocate-step', AllocateStep, {
    amount: allocateAmount,
    attemptingTxn: allocateAttemptingTxn,
    pendingText: (
      <>
        Staking {allocateAmount?.toSignificant(6)} {allocateAmount?.currency.symbol}
      </>
    ),
    hash: allocateHash,
    autoConfirm: true,
    transactionWaiting: true,
    onAllocate,
  })
  const allocateSteps = [allocateStep, approveUsageStep, allocateConfirmStep]

  const {
    amount: deallocateAmount,
    attemptingTxn: deallocateAttemptingTxn,
    hash: deallocateHash,
    onDeallocate,
    reset: resetDeallocate,
  } = useDeallocateState()

  const deallocateStep = {
    ...useStepWithNext('DeallocateViewStep', DeallocateViewStep, {}),
    onBack,
  }
  const deallocateConfirmStep = useStepWithNext('DeallocateStep', DeallocateStep, {
    amount: deallocateAmount,
    attemptingTxn: deallocateAttemptingTxn,
    pendingText: (
      <>
        Unstaking {deallocateAmount?.toSignificant(6)} {deallocateAmount?.currency.symbol}
      </>
    ),
    hash: deallocateHash,
    autoConfirm: true,
    transactionWaiting: true,
    onDeallocate,
  })
  const deallocateSteps = [deallocateStep, deallocateConfirmStep]
  const steps = isModalOpen === 'allocate' ? allocateSteps : deallocateSteps

  const handleOpenModal = (mode: 'allocate' | 'deallocate') => {
    resetAllocate()
    resetDeallocate()
    onBack()
    setIsModalOpen(mode)
  }

  const handleFinish = () => {
    resetAllocate()
    resetDeallocate()
    onBack()
    clear()
  }
  return (
    <>
      <StepLineModal
        steps={steps}
        isOpen={isModalOpen !== false}
        onDismiss={handleFinish}
        onFinish={handleFinish}
        header={isModalOpen === 'allocate' ? 'Stake' : 'Unstake'}
      />
      <Row width="100%" gap="8px">
        <ButtonPrimary onClick={() => handleOpenModal('allocate')}>Stake</ButtonPrimary>
        <ButtonPrimary onClick={() => handleOpenModal('deallocate')}>Unstake</ButtonPrimary>
      </Row>
    </>
  )
}

export default ManagerAllocationButtons
